import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { GetUpcomingDemoRequestsCall } from "../redux/api/demoRequest.api";

const POLL_INTERVAL_MS = 60 * 1000;

const UpcomingDemosContext = createContext({
  items: [],
  total: 0,
  todayCount: 0,
  loading: false,
  isOpen: false,
  enabled: false,
  openModal: () => {},
  closeModal: () => {},
  refresh: () => {},
});

/*
  Provides the upcoming-demos data + a global "open modal" handle so the
  Sidenav badge and the modal itself can share state without prop-drilling.
  No-op for non-admins so any non-admin role pays zero network cost.
*/
export function UpcomingDemosProvider({ children }) {
  const role = useSelector((s) => s?.auth?.role);
  const accessToken = useSelector((s) => s?.auth?.access_token);
  const enabled =
    Boolean(accessToken) && String(role || "").toLowerCase() === "admin";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setItems([]);
      setTotal(0);
      setTodayCount(0);
      return;
    }
    setLoading(true);
    const res = await GetUpcomingDemoRequestsCall({ limit: 25 });
    if (!mountedRef.current) return;
    setItems(Array.isArray(res?.items) ? res.items : []);
    setTotal(Number(res?.total || 0));
    setTodayCount(Number(res?.todayCount || 0));
    setLoading(false);
  }, [enabled]);

  // Initial load + interval polling while enabled.
  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled, refresh]);

  const openModal = useCallback(() => {
    setIsOpen(true);
    // Refresh on open so the admin always sees fresh data (no stale 60s lag).
    refresh();
  }, [refresh]);

  const closeModal = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({
      items,
      total,
      todayCount,
      loading,
      isOpen,
      enabled,
      openModal,
      closeModal,
      refresh,
    }),
    [items, total, todayCount, loading, isOpen, enabled, openModal, closeModal, refresh]
  );

  return (
    <UpcomingDemosContext.Provider value={value}>
      {children}
    </UpcomingDemosContext.Provider>
  );
}

UpcomingDemosProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useUpcomingDemos() {
  return useContext(UpcomingDemosContext);
}

export default UpcomingDemosContext;
